import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import mapboxgl from "mapbox-gl";
type Map = mapboxgl.Map;
type MapOptions = mapboxgl.MapOptions;
import "mapbox-gl/dist/mapbox-gl.css";
import { colors } from "@/styles/colors";
import { findRouteBounds } from "./routeUtils";
import { ElevationChart } from "./elevationChart";
import { Route } from "@/apiTypes/event";
import { z } from "zod";
import { lineString, along, length, bearing, point } from "@turf/turf";
import type { Feature, FeatureCollection, LineString } from "geojson";
import { useMap } from "@/hooks/useMap";

const ARROW_SPACING_KM = 10;

/**
 * Generates evenly-spaced points along a line with bearing for direction arrows.
 */
function generateArrowPoints(line: Feature<LineString>): FeatureCollection {
  const totalLength = length(line, { units: "kilometers" });
  const features: Feature[] = [];

  for (
    let dist = ARROW_SPACING_KM;
    dist < totalLength;
    dist += ARROW_SPACING_KM
  ) {
    const pt = along(line, dist, { units: "kilometers" });
    const nextPt = along(line, dist + 0.1, { units: "kilometers" });
    const angle = bearing(pt, nextPt);
    pt.properties = { bearing: angle };
    features.push(pt);
  }

  return { type: "FeatureCollection", features };
}

interface FeatureMapProps {
  route: z.infer<typeof Route>;
  options?: Omit<MapOptions, "container">;
}

export const FeatureMap: FC<FeatureMapProps> = ({ route, options }) => {
  const { map, ref } = useMap(options);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!map) return;

    // Check if map container is still valid (HMR safety)
    try {
      const container = map.getContainer();
      if (!container || !container.parentNode) {
        return;
      }
    } catch {
      return;
    }

    // Create checkerboard image for finish marker if not already added
    if (!map.hasImage("finish-checkerboard")) {
      const ratio = window.devicePixelRatio || 1;
      const size = 20 * ratio;
      const radius = size / 2;
      const strokeWidth = 3 * ratio;
      const innerRadius = radius - strokeWidth / 2;
      const cells = 4;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        // Clip to circle and draw checkerboard inside
        ctx.beginPath();
        ctx.arc(radius, radius, innerRadius, 0, Math.PI * 2);
        ctx.closePath();
        ctx.save();
        ctx.clip();

        const cellSize = (innerRadius * 2) / cells;
        const origin = radius - innerRadius;
        for (let row = 0; row < cells; row++) {
          for (let col = 0; col < cells; col++) {
            ctx.fillStyle = (row + col) % 2 === 0 ? "#000000" : "#ffffff";
            ctx.fillRect(
              origin + col * cellSize,
              origin + row * cellSize,
              cellSize,
              cellSize,
            );
          }
        }
        ctx.restore();

        // Draw circle border
        ctx.beginPath();
        ctx.arc(radius, radius, innerRadius, 0, Math.PI * 2);
        ctx.strokeStyle = colors.primary;
        ctx.lineWidth = strokeWidth;
        ctx.stroke();

        map.addImage(
          "finish-checkerboard",
          ctx.getImageData(0, 0, size, size),
          { pixelRatio: ratio },
        );
      }
    }

    // Create chevron image for direction indicators if not already added
    if (!map.hasImage("route-chevron")) {
      const ratio = window.devicePixelRatio || 1;
      const size = 16 * ratio;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2.5 * ratio;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        // Chevron pointing up (north = 0°)
        ctx.moveTo(size * 0.2, size * 0.65);
        ctx.lineTo(size * 0.5, size * 0.35);
        ctx.lineTo(size * 0.8, size * 0.65);
        ctx.stroke();

        map.addImage(
          "route-chevron",
          ctx.getImageData(0, 0, size, size),
          { pixelRatio: ratio },
        );
      }
    }

    // useMap defers exposing the map until the style is loaded,
    // so we can add sources and layers directly.
    if (!map.getSource("dem")) {
      map.addSource("dem", {
        type: "raster-dem",
        url: "mapbox://mapbox.mapbox-terrain-dem-v1",
      });
      map.addLayer(
        {
          id: "hillshading",
          source: "dem",
          type: "hillshade",
        },
        "land-structure-polygon",
      );
    }

    if (route.track_points && Array.isArray(route.track_points)) {
      const coordinates = route.track_points
        .filter((point) => point?.lat !== undefined && point.lng !== undefined)
        .map((point) => [point.lng!, point.lat!]);

      if (coordinates.length > 0) {
        const line = lineString(coordinates);
        const geojsonData: FeatureCollection = {
          type: "FeatureCollection",
          features: [line],
        };

        if (map.getSource("route")) {
          (map.getSource("route") as mapboxgl.GeoJSONSource).setData(
            geojsonData,
          );
        } else {
          map.addSource("route", {
            type: "geojson",
            data: geojsonData,
          });

          map.addLayer({
            id: "route",
            type: "line",
            source: "route",
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": colors.primary,
              "line-width": 5,
            },
          });
        }

        // Direction arrows along the route
        const arrowData = generateArrowPoints(line);

        if (map.getSource("route-arrows")) {
          (map.getSource("route-arrows") as mapboxgl.GeoJSONSource).setData(
            arrowData,
          );
        } else {
          map.addSource("route-arrows", {
            type: "geojson",
            data: arrowData,
          });

          map.addLayer({
            id: "route-arrows",
            type: "symbol",
            source: "route-arrows",
            layout: {
              "icon-image": "route-chevron",
              "icon-size": 0.6,
              "icon-rotate": ["get", "bearing"],
              "icon-rotation-alignment": "map",
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
            },
          });
        }

        // Start marker at the first coordinate
        const startPoint = point(coordinates[0]);
        const startData: FeatureCollection = {
          type: "FeatureCollection",
          features: [startPoint],
        };

        if (map.getSource("route-start")) {
          (map.getSource("route-start") as mapboxgl.GeoJSONSource).setData(
            startData,
          );
        } else {
          map.addSource("route-start", {
            type: "geojson",
            data: startData,
          });

          map.addLayer({
            id: "route-start",
            type: "circle",
            source: "route-start",
            paint: {
              "circle-radius": 8,
              "circle-color": "#ffffff",
              "circle-stroke-color": colors.primary,
              "circle-stroke-width": 3,
            },
          });
        }

        // Finish marker at the last coordinate
        const finishPoint = point(coordinates[coordinates.length - 1]);
        const finishData: FeatureCollection = {
          type: "FeatureCollection",
          features: [finishPoint],
        };

        if (map.getSource("route-finish")) {
          (map.getSource("route-finish") as mapboxgl.GeoJSONSource).setData(
            finishData,
          );
        } else {
          map.addSource("route-finish", {
            type: "geojson",
            data: finishData,
          });

          map.addLayer({
            id: "route-finish",
            type: "symbol",
            source: "route-finish",
            layout: {
              "icon-image": "finish-checkerboard",
              "icon-size": 1,
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
            },
          });
        }

        // Waypoint markers
        const emptyCollection: FeatureCollection = {
          type: "FeatureCollection",
          features: [],
        };
        const waypointData: FeatureCollection =
          route.waypoints && route.waypoints.length > 0
            ? {
                type: "FeatureCollection",
                features: route.waypoints
                  .filter((wp) => wp.lat != null && wp.lng != null)
                  .map((wp) =>
                    point([wp.lng, wp.lat], {
                      name: wp.name || "Waypoint",
                      description: wp.description || "",
                    }),
                  ),
              }
            : emptyCollection;

        if (map.getSource("route-waypoints")) {
          (map.getSource("route-waypoints") as mapboxgl.GeoJSONSource).setData(
            waypointData,
          );
        } else {
          map.addSource("route-waypoints", {
            type: "geojson",
            data: waypointData,
          });

          map.addLayer({
            id: "route-waypoints",
            type: "circle",
            source: "route-waypoints",
            paint: {
              "circle-radius": 6,
              "circle-color": colors.secondary,
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 2,
            },
          });
        }

        const bounds = findRouteBounds(geojsonData);

        if (!bounds.isEmpty()) {
          map.fitBounds(bounds, {
            padding: { top: 50, right: 50, left: 50, bottom: hasElevationData ? 140 : 50 },
            maxZoom: 14,
            duration: 800,
          });
        }
      }
    }
  }, [map, route]);

  useEffect(() => {
    if (!map) return;
    map.resize();
  }, [map, isFullscreen]);

  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFullscreen]);

  // Waypoint hover popup
  useEffect(() => {
    if (!map) return;
    if (!route.waypoints || route.waypoints.length === 0) return;
    if (!map.getLayer("route-waypoints")) return;

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 10,
    });

    const onEnter = (e: mapboxgl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = "pointer";
      const feature = e.features?.[0];
      if (!feature || feature.geometry.type !== "Point") return;

      const coords = (feature.geometry as GeoJSON.Point).coordinates as [
        number,
        number,
      ];
      const name = feature.properties?.name || "Waypoint";
      const description = feature.properties?.description || "";

      popup
        .setLngLat(coords)
        .setHTML(
          `<div style="font-family: sans-serif; font-size: 13px; line-height: 1.3;">` +
            `<strong>${name}</strong>` +
            (description
              ? `<div style="color: #666; margin-top: 2px;">${description}</div>`
              : "") +
            `</div>`,
        )
        .addTo(map);
    };

    const onLeave = () => {
      map.getCanvas().style.cursor = "";
      popup.remove();
    };

    map.on("mouseenter", "route-waypoints", onEnter);
    map.on("mouseleave", "route-waypoints", onLeave);

    return () => {
      map.off("mouseenter", "route-waypoints", onEnter);
      map.off("mouseleave", "route-waypoints", onLeave);
      popup.remove();
    };
  }, [map, route.waypoints]);

  const hasElevationData =
    route.track_points?.some((p) => p.ele !== undefined && p.d !== undefined) ??
    false;

  return (
    <div
      className={
        isFullscreen ? "fixed inset-0 z-50 bg-white" : "relative h-full w-full"
      }
    >
      <div className="h-full w-full" ref={ref} />
      <button
        type="button"
        onClick={() => setIsFullscreen((prev) => !prev)}
        className="absolute top-2 right-2 z-10 rounded bg-white p-1.5 shadow-md hover:bg-gray-100"
        aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
      >
        {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
      </button>
      {map && hasElevationData && (
        <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-[25%] max-h-32 overflow-hidden bg-white/60 backdrop-blur-sm">
          <ElevationChart trackPoints={route.track_points!} map={map} />
        </div>
      )}
    </div>
  );
};
